import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

export default function Contact() {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  const contactInfo = [
    { icon: MapPin, label: 'Address', value: t('contact.info.address'), link: 'https://share.google/tkb47jzKDeiANfZNX' },
    { icon: Phone, label: 'Phone', value: t('contact.info.phone'), link: 'tel:03715222367' },
    { icon: Mail, label: 'Email', value: t('contact.info.email'), link: 'mailto:aarogyaent@gmail.com' },
    { icon: Clock, label: 'Hours', value: t('contact.info.hours') }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts`, {
        ...formData,
        language: i18n.language
      });
      toast.success(t('contact.form.success'));
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      toast.error(t('contact.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6" data-testid="contact-page-title">
            {t('contact.title')}
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground" data-testid="contact-page-subtitle">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <Card className="p-8 lg:p-12">
            <h2 className="text-2xl font-bold text-foreground mb-8">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="contact-name">{t('contact.form.name')}</Label>
                <Input
                  id="contact-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="contact-name-input"
                  className="mt-2"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="contact-phone">{t('contact.form.phone')}</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="contact-phone-input"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email">{t('contact.form.email')}</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="contact-email-input"
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contact-message">{t('contact.form.message')}</Label>
                <Textarea
                  id="contact-message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={6}
                  required
                  data-testid="contact-message-textarea"
                  className="mt-2"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full btn-primary"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
              </Button>
            </form>
          </Card>

          <div className="space-y-6">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-foreground mb-8">Contact Information</h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4" data-testid={`contact-info-${index}`}>
                    <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                      <info.icon size={24} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-muted-foreground mb-1">{info.label}</p>
                      {info.link ? (
                        <a href={info.link} target={info.label === 'Address' ? '_blank' : undefined} rel={info.label === 'Address' ? 'noopener noreferrer' : undefined} className="text-foreground hover:text-primary transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-foreground">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-0 overflow-hidden">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3570.5!2d93.7!3d26.7!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDQyJzAwLjAiTiA5M8KwNDInMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Aarogya ENT Clinic Location"
                data-testid="clinic-map"
              ></iframe>
              <div className="p-6 bg-secondary/30">
                <h3 className="text-lg font-semibold text-foreground mb-2">Visit Our Clinic</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Experience modern facilities in a warm and welcoming environment
                </p>
                <a 
                  href="https://share.google/tkb47jzKDeiANfZNX" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                  data-testid="get-directions-link"
                >
                  <MapPin size={16} />
                  Get Directions
                </a>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
